import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import ErrorBoundary from './components/ErrorBoundary';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { supabase } from './services/supabase';

// Global error handler to catch ALL errors including those before React renders
window.onerror = function(message, source, lineno, colno, error) {
  console.error('🔴 GLOBAL ERROR:', { message, source, lineno, colno, error });
  // Store in localStorage so we can retrieve it
  try {
    localStorage.setItem('lastGlobalError', JSON.stringify({
      message: String(message),
      source,
      lineno,
      colno,
      stack: error?.stack,
      time: new Date().toISOString()
    }));
  } catch (e) {}
  return false;
};

// Catch unhandled promise rejections
window.onunhandledrejection = function(event) {
  console.error('🔴 UNHANDLED PROMISE REJECTION:', event.reason);
  try {
    localStorage.setItem('lastUnhandledRejection', JSON.stringify({
      reason: String(event.reason),
      stack: event.reason?.stack,
      time: new Date().toISOString()
    }));
  } catch (e) {}
};

// Handle magic link authentication from URL hash BEFORE app renders
// Supabase's detectSessionInUrl should handle this automatically, but we log for debugging
if (window.location.hash && window.location.hash.includes('access_token')) {
  console.log('Magic link detected in URL hash, Supabase will process automatically...');
  console.log('Hash contents:', window.location.hash.substring(0, 100) + '...');
}

// Clear chunk error reload flag on successful app load
// This prevents the flag from persisting after a successful reload
try {
  sessionStorage.removeItem('chunk_error_reload');
} catch (e) {
  // sessionStorage not available
}

// Create a client with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data considered fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // Cache kept for 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false, // Don't refetch on window focus (can be annoying)
      refetchOnReconnect: true, // Refetch when internet reconnects
      retry: 1, // Retry failed queries once
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <App />
        </HelmetProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
